import React, { memo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import clone from 'ramda/src/clone';
import concat from 'ramda/src/concat';
import equals from 'ramda/src/equals';
import findIndex from 'ramda/src/findIndex';
import isEmpty from 'ramda/src/isEmpty';
import length from 'ramda/src/length';
import move from 'ramda/src/move';
import propEq from 'ramda/src/propEq';
import remove from 'ramda/src/remove';
import Button from '@components/@core/AnimatedButton';
import EditDropdown from '@components/EditDropdown';
import Filter from '@components/@core/Filter';
import Icon from '@components/@core/Icon';
import Input from '@components/@core/Input';
import Settings from '@components/@core/Settings';
import Thumbnail from '@components/@core/Thumbnail';
import AppActions from '~/actions/app';
import DevelopActions from '~/actions/develop';
import { LibraryCardEditMenu } from '~/services/config';
import Course from './Course';

const Edit = ({ data, onCardMenu, onChildMenu }) => {
    const [name, setName] = useState(data.title || data.data.title);
    const [description, setDescription] = useState(data.description || data.data?.description);
    const [thumbnail, setThumbnail] = useState(data.data?.thumbnail || data.thumbnail);
    const [objectives, setObjectives] = useState(data.data?.objectives || []);
    const [selected, setSelected] = useState({
        author: [data.author_id],
        department: data.data.department,
        competency: data.data.competency,
        category: data.data.category,
    });
    const [chronologicalLock, setChronologicalLock] = useState(data.data?.chronological_lock || false);
    const [timeLock, setTimeLock] = useState(data.data?.time_lock || false);
    const [courses, setCourses] = useState(data.data.cards || []);

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const disabled =
        equals(name, data.title || data.data.title) &&
        equals(description, data.description || data.data?.description) &&
        equals(selected.author, [data.author_id]) &&
        equals(selected.department, data.data.department) &&
        equals(selected.competency, data.data.competency) &&
        equals(selected.category, data.data.category) &&
        equals(objectives, data.data?.objectives || []) &&
        equals(chronologicalLock, data.data?.chronological_lock || false) &&
        equals(timeLock, data.data?.time_lock || false) &&
        equals(thumbnail, data.data?.thumbnail || data.thumbnail) &&
        equals(courses, data.data.cards || []);

    const handleSelectTags = (key) => (tags) => {
        const _selected = clone(selected);
        _selected[key] = tags;
        setSelected(_selected);
    };
    const handleSubmit = (published) => () => {
        const _objectives = objectives.filter((e) => !!e);
        const _courses = courses.map((item) => ({
            alert_manager: null,
            blocked_by: item.blocked_by,
            card_type: 'course',
            card_template_id: item.id,
            complete_track: null,
        }));

        const payload = {
            track: {
                id: data.id,
                user_id: null,
                title: name,
                status: 0,
                data: {
                    cards: _courses,
                    category: selected.category,
                    competency: selected.competency,
                    department: selected.department,
                    objectives: _objectives,
                },
                result: null,
                deleted_at: null,
                author_id: selected.author[0],
                type: 1,
                completed_at: null,
                designation: 1,
                program_id: null,
                archived: 0,
                description,
                start_at: null,
                end_at: null,
                chronological_lock: chronologicalLock,
                time_lock: timeLock,
                program_level: 1,
                published,
                thumbnail: thumbnail || '',
            },
        };

        const after = history.location.pathname.includes('view')
            ? {
                  type: 'POSTLIBRARYCARDTEMPLATES_REQUEST',
                  target: 'tracks',
                  payload: { data: payload.track },
              }
            : { type: 'GETLIBRARYTEMPLATESSEARCH_REQUEST', target: 'tracks' };

        dispatch(DevelopActions.librarytemplatesaveRequest(payload, (data.data?.thumbnail || data.thumbnail) === thumbnail ? null : [thumbnail], 'tracks', after, '/library/training?active=tracks'));
    };
    const handleAttachCourses = () => {
        dispatch(
            AppActions.modalRequest({
                type: 'Attach Library',
                data: { before: { show: ['courses'] } },
                callBack: {
                    onAttach: (e) => {
                        const _courses = concat(courses, e.templates);
                        setCourses(_courses);
                    },
                },
            })
        );
    };
    const handleObjectiveChange = (index) => (e) => {
        const _objectives = clone(objectives);
        _objectives[index] = e;
        setObjectives(_objectives);
    };
    const handleObjectiveAdd = () => {
        const _objectives = clone(objectives);
        _objectives.push('');
        setObjectives(_objectives);
    };
    const handleObjectiveDelete = (index) => () => {
        const _objectives = clone(objectives);
        _objectives.splice(index, 1);
        setObjectives(_objectives);
    };
    const handleChildMenu = (event, item) => {
        const index = findIndex(propEq('id', item.id), courses);
        if (index > -1) {
            let _courses = clone(courses);
            if (event === 'move up') {
                if (index === 0) return;
                _courses = move(index, index - 1, _courses);
            } else if (event === 'move down') {
                if (_courses?.length === index + 1) return;
                _courses = move(index, index + 1, _courses);
            } else if (event === 'remove') {
                _courses = remove(index, 1, _courses);
            } else {
                onChildMenu(event, item);
            }
            setCourses(_courses);
        }
    };

    const role = useSelector((state) => state.app.app_role_id);
    const authors = useSelector((state) => state.app.authors);
    const departments = useSelector((state) => state.app.departments);
    const competencies = useSelector((state) => state.app.competencies);
    const categories = useSelector((state) => state.app.categories);

    const handleSelectMenu = () => (event, course) => {
        const quotaLen = length(courses);
        let clonedCourses = clone(courses);
        const indx = findIndex(propEq('id', course.id), clonedCourses);
        if (indx < 0) return;

        switch (event) {
            case 'move up':
                if (indx !== 0) clonedCourses = move(indx, indx - 1, modules);
                break;
            case 'move down':
                if (quotaLen !== indx + 1) clonedCourses = move(indx, indx + 1, modules);
                break;
            case 'remove':
                clonedCourses = remove(indx, 1, clonedCourses);
                break;
        }
        setCourses(clonedCourses);
    };

    const settings = [
        { name: 'Author', id: selected.author, options: authors, select: 'author' },
        { name: 'Departments', id: isEmpty(data.data.category) ? null : data.data.category, options: departments, select: 'department' },
        { name: 'Competencies', id: isEmpty(data.data.competency) ? null : data.data.competency, options: competencies, select: 'competency' },
        { name: 'Categories', id: isEmpty(data.data.department) ? null : data.data.department, options: categories, select: 'category' },
    ];

    return (
        <>
            <Filter back="all tracks" />
            <div className="library-track-card border-5" data-cy="editTrackForm">
                <div className="detail-content px-3 px-sm-0">
                    <div className="d-flex jc-between mb-3 pt-3">
                        <span className="dsl-b22 bold" data-cy="editTrackTitle">
                            Track
                        </span>
                        <EditDropdown options={LibraryCardEditMenu[role]} onChange={onCardMenu} dataCy="editTrackTopThreeDot" />
                    </div>
                    <div className="d-flex jc-between ssm-column">
                        <div className="ssm-100 w-50 w-sm-100 mb-3">
                            <div className="pl-1 mb-3">
                                <Input className="course-input mb-3" title="Name" dataCy="title" value={name} maxLength={30} placeholder="Type here..." onChange={(e) => setName(e)} />
                            </div>
                            <div className="pl-1 mb-3">
                                <Input
                                    className="course-text"
                                    title="Description"
                                    dataCy="description"
                                    placeholder="Type here..."
                                    as="textarea"
                                    rows="3"
                                    maxLength={100}
                                    value={description}
                                    onChange={(e) => setDescription(e)}
                                />
                            </div>
                            <div className="d-flex ai-start ps-1">
                                <p className="career-text core-input-label dsl-b12 pt-2 m-0">Learning Objectives</p>
                                <div className="d-flex flex-column w-100">
                                    {objectives.map((item, index) => (
                                        <Input
                                            className="course-input mb-2 d-flex-1"
                                            value={objectives[index]}
                                            placeholder="Type here..."
                                            direction="vertical"
                                            onChange={handleObjectiveChange(index)}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="ssm-100 w-40 w-sm-100">
                            <Thumbnail type="upload" isDisablePicker source={thumbnail} size="medium" label="Upload image" onDrop={(e) => setThumbnail(e)} onDelete={() => setThumbnail(null)} />
                        </div>
                    </div>
                </div>
            </div>

            <div className="library-track-card mt-2 pt-3 px-3 border-5" data-cy="includedCourse">
                <div className="modules-content pb-0" dataCy="courseLists">
                    <div className="d-flex">
                        <p className="dsl-b22 bold">Courses Attached</p>
                    </div>
                    <Course data={courses} role={role} onMenu={handleChildMenu} onSelect={handleSelectMenu()} edit />
                    <div className="d-h-start justify-content-end my-3 my-sm-0 pb-2 pt-4">
                        <Button className="ml-auto ps-0 d-flex" type="link" onClick={handleAttachCourses}>
                            <Icon name="fas fa-plus" size={14} color="#376caf" />
                            <span className="dsl-p14 text-400 ms-1">Attach Course</span>
                        </Button>
                    </div>
                </div>
            </div>
            <Settings isEditable expanded values={settings} handleSelect={handleSelectTags} />
            <div className="mt-3 d-flex jc-end">
                <Button className="mr-3" name="Draft" disabled={data?.published ? false : disabled} type="low" onClick={handleSubmit(0)} />
                <Button name="Publish" disabled={data?.published ? disabled : false} onClick={handleSubmit(1)} />
            </div>
        </>
    );
};

export default memo(Edit);
